import { Button } from '@progress/kendo-react-buttons'
import classnames from 'classnames'
import isNil from 'lodash/fp/isNil'
import Moment from 'moment'
import React from 'react';
import { connect } from 'react-redux'
import { Col, Row } from 'reactstrap'
import Redux from 'redux'
import * as DetailPanel from '../../../components/detail-panel'
import * as Form from '../../../components/form'
import { Loading } from '../../../components/loading'
import * as RasaInput from '../../../components/rasa-input'
import * as RootConstants from '../../../constants'
import * as Schedules from '../../schedules'
import * as Attributes from '../attributes'
import * as MemberTypes from '../types'
import * as MemberUtils from '../utils'
import * as Actions from './actions'
import * as Utils from './utils'

interface FieldProps {
  label: string,
  value: string,
}
const DateTimeField = ({label, value}: FieldProps) => {
  return <Row>
    <Col md="4" className="key">{label}:</Col>
    <Col md="8" className="value">{value ?
      Moment(value).format(RootConstants.DATETIME_FORMAT) : 'N/A'}</Col>
  </Row>
}

const OptionalStringField = ({label, value}: FieldProps) => {
  return isNil(value) ? null : <Row>
    <Col md="4" className="key">{label}:</Col>
    <Col md="8" className="value">{value}</Col>
  </Row>
}

interface ComponentState {
  details: MemberTypes.MemberDetail,
  form: Form.State<MemberTypes.MemberDetailFormFields>,
  schedules: Schedules.ScheduleState,
}

interface DispatchActions {
  addAttribute: Attributes.AddAttributeFn,
  cancel: Redux.ActionCreator<Form.FormAction<{}>>,
  change: Redux.ActionCreator<Form.UpdateFieldAction>,
  close: Redux.ActionCreator<DetailPanel.DetailPanelAction<void>>,
  enter: Redux.ActionCreator<Form.FormAction<Form.Field<string>>>,
  save: Redux.ActionCreator<Form.FormAction<MemberTypes.MemberDetailFormFields>>,
}

interface DispatchState {
  actions: DispatchActions
}

const Component = ({actions, details, form, schedules}: ComponentState & DispatchState) => {
  return <DetailPanel.DetailPanel >
        <div className={classnames({empty: !details})}>
          <Row className="detail-panel-header">
            <Col sm="12" md="1">
              <h4>Details</h4>
            </Col>
            <Col sm="12" md="8">
            {form.isEditing && form.isSaving &&
              <span>
                <Loading size={Loading.Sizes.SMALL} />
              </span>}
            {form.isEditing && !form.isSaving &&
              <span>
                <Button primary
                        disabled={false /*TODO validate form*/}
                        onClick={() => actions.save(MemberUtils.getMemberDetailSaveParams(details, form))}>
                  Save
                </Button>
                <Button className="secondary-button" onClick={actions.cancel}>
                  Cancel
                </Button>
              </span>}
            {!form.isEditing &&
              <span>
                <Button primary onClick={() => actions.enter(MemberUtils.getMemberDetailFields(details))}>
                  Edit
                </Button>
              </span>}
            </Col>
            <Col sm="12" md="2">
            {form.isEditing &&
              <span>
                <Button className="secondary-button" onClick={actions.addAttribute}>
                  Add Attribute
                </Button>
              </span>}
            </Col>
            <Col sm="12" md="1">
              <Button icon="close" onClick={actions.close} className="close">
              </Button>
            </Col>
          </Row>
          <Row>
            <Col md="4">
              <Row>
                <Col md="4" className="key">First Name:</Col>
                <Col md="8" className="value">
                  {form.isEditing ?
                    <RasaInput.RasaInput
                      validation={RasaInput.validatePresent(form.fields.first_name)}
                      disabled={form.isSaving}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        actions.change({name: 'first_name', value: e.target.value})}
                      value={form.fields.first_name}
                    />
                    : details.first_name}
                </Col>
              </Row>
              <Row>
                <Col md="4" className="key">Last Name:</Col>
                <Col md="8" className="value">
                  {form.isEditing ?
                    <RasaInput.RasaInput
                      validation={RasaInput.validatePresent(form.fields.last_name)}
                      disabled={form.isSaving}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        actions.change({name: 'last_name', value: e.target.value})}
                      value={form.fields.last_name}
                    />
                    : details.last_name}
                </Col>
              </Row>
              <Row>
                <Col md="4" className="key">Email:</Col>
                <Col md="8" className="value">
                  {form.isEditing ?
                    <RasaInput.RasaInput
                      validation={RasaInput.validateEmail(form.fields.email)}
                      disabled={form.isSaving}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        actions.change({name: 'email', value: e.target.value})}
                      value={form.fields.email}
                    />
                    : details.email}
                </Col>
              </Row>
              <Row>
                <Col md="4" className="key">Schedule:</Col>
                <Col md="8" className="value">
                  {form.isEditing ?
                    <RasaInput.RasaSelectInput
                      options={Schedules.asDropdown(schedules)}
                      disabled={form.isSaving}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        actions.change({name: 'schedule_id', value: parseInt(e.target.value, 10)})}
                      value={form.fields.schedule_id || 0}
                    />
                    : Utils.getSchedule(schedules, details.schedule_id).name}
                </Col>
              </Row>
            </Col>
            <Col md="4">
              <DateTimeField label="Last Open" value={details.last_open}/>
              <DateTimeField label="Last Click" value={details.last_click}/>
              <DateTimeField label="Last Newsletter" value={details.last_newsbrief}/>
              <DateTimeField label="Created" value={details.created}/>
              <DateTimeField label="Updated" value={details.updated}/>
              <OptionalStringField label="Referred By" value={details.referred_by}/>
              <OptionalStringField label="Partner Code" value={details.partner_code}/>
            </Col>
            <Col md="4">
              <div className="member-detail-last-column">
                <Attributes.MemberAttributesGrid />
              </div>
            </Col>
          </Row>
        </div>
      </DetailPanel.DetailPanel>
}

export const MemberDetailPanel = connect(
  ({members, schedules}: MemberTypes.WrappedState & Schedules.WrappedState) => ({
    details: members.memberDetail,
    form: members.detailForm,
    schedules: schedules.data,
  }),
  {
    addAttribute: Attributes.addAttribute,
    cancel: Actions.EditFormActions[Form.CANCEL],
    change: Actions.EditFormActions[Form.UPDATE],
    close: Actions.DetailPanelActions[DetailPanel.CLOSE],
    enter: Actions.EditFormActions[Form.ENTER],
    save: Actions.EditFormActions[Form.SAVE],
  },
  (stateProps, dispatchProps) => ({
    ...stateProps,
    actions: dispatchProps,
  }),
)(Component)



// WEBPACK FOOTER //
// ./src/pages/members/details/panel.tsx